import { openDB } from 'idb';
import { AbstractLogger, isSameOrHigherLevel, safeStringifyLogContext } from '@signage-player/logger';
const DEFAULT_OPTIONS = {
    filePrefix: 'app',
    daysToKeep: 14,
    maxTotalSize: 10 * 1024 * 1024,
    cleanupIntervalMs: 60000,
    flushIntervalMs: 250,
    flushBatchSize: 50,
    usePrefix: true,
};
export class IndexedDBLogger extends AbstractLogger {
    constructor(minimumLevel, options) {
        super(minimumLevel);
        this.dbPromise = null;
        this.queue = [];
        this.flushTimer = null;
        this.cleanupTimer = null;
        this.destroyed = false;
        this.flushing = Promise.resolve();
        this.opts = { ...DEFAULT_OPTIONS, ...options };
        this.dbName = `${this.opts.filePrefix}-logs`;
        if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
            navigator.storage.persist().catch(() => {
                // best-effort; some browsers prompt for permission and may deny
            });
        }
        this.cleanupTimer = setInterval(() => {
            this.forceCleanup().catch(err => console.error('[IndexedDBLogger] cleanup error', err));
        }, this.opts.cleanupIntervalMs);
        this.forceCleanup().catch(() => { });
    }
    async db() {
        if (!this.dbPromise) {
            this.dbPromise = openDB(this.dbName, 1, {
                upgrade(db) {
                    const store = db.createObjectStore('entries', { keyPath: 'id', autoIncrement: true });
                    store.createIndex('byTimestamp', 'timestamp');
                    store.createIndex('byLevel', 'level');
                },
            });
        }
        return this.dbPromise;
    }
    // LogManager.setChannelLevel duck-types on this — without it, remote
    // log-level overrides silently no-op for the persistent channel.
    setMinimumLevel(level) {
        this.minimumLevel = level;
    }
    getMinimumLevel() {
        return this.minimumLevel;
    }
    log(level, message, context) {
        if (this.destroyed)
            return;
        if (!isSameOrHigherLevel(level, this.minimumLevel))
            return;
        const cleaned = this.clearContext(context);
        const record = {
            timestamp: Date.now(),
            level,
            message,
            context: cleaned ? safeStringifyLogContext(cleaned) : null,
        };
        this.queue.push(record);
        if (this.queue.length >= this.opts.flushBatchSize) {
            this.scheduleFlush(0);
        }
        else {
            this.scheduleFlush(this.opts.flushIntervalMs);
        }
    }
    scheduleFlush(delay) {
        if (this.flushTimer)
            return;
        this.flushTimer = setTimeout(() => {
            this.flushTimer = null;
            this.flushing = this.doFlush();
        }, delay);
    }
    async doFlush() {
        if (this.queue.length === 0)
            return;
        const batch = this.queue.splice(0, this.queue.length);
        try {
            const db = await this.db();
            const tx = db.transaction('entries', 'readwrite');
            for (const record of batch) {
                tx.store.put(record);
            }
            await tx.done;
        }
        catch (err) {
            console.error('[IndexedDBLogger] Failed to flush batch', err);
        }
    }
    async flush() {
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
        this.flushing = this.doFlush();
        await this.flushing;
    }
    async destroy() {
        if (this.destroyed)
            return;
        this.destroyed = true;
        if (this.cleanupTimer) {
            clearInterval(this.cleanupTimer);
            this.cleanupTimer = null;
        }
        await this.flush();
        if (this.dbPromise) {
            const db = await this.dbPromise;
            db.close();
            this.dbPromise = null;
        }
    }
    async forceCleanup() {
        try {
            const cutoff = Date.now() - this.opts.daysToKeep * 24 * 60 * 60 * 1000;
            const db = await this.db();
            const tx = db.transaction('entries', 'readwrite');
            const index = tx.store.index('byTimestamp');
            let cursor = await index.openCursor(IDBKeyRange.upperBound(cutoff));
            while (cursor) {
                await cursor.delete();
                cursor = await cursor.continue();
            }
            await tx.done;
            if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
                const { usage = 0 } = await navigator.storage.estimate();
                if (usage > this.opts.maxTotalSize) {
                    await this.trimToSize();
                }
            }
        }
        catch (err) {
            console.error('[IndexedDBLogger] forceCleanup failed', err);
        }
    }
    async trimToSize() {
        const db = await this.db();
        const tx = db.transaction('entries', 'readwrite');
        // Count first — awaiting another request while a cursor is open
        // can auto-close the cursor in real IDB (fake-indexeddb masks this).
        const total = await tx.store.count();
        const target = Math.max(100, Math.floor(total / 10));
        const index = tx.store.index('byTimestamp');
        let cursor = await index.openCursor();
        let deleted = 0;
        while (cursor && deleted < target) {
            await cursor.delete();
            deleted++;
            cursor = await cursor.continue();
        }
        await tx.done;
    }
    async downloadLogs() {
        const db = await this.db();
        const all = await db.getAll('entries');
        const lines = all
            .sort((a, b) => a.timestamp - b.timestamp)
            .map(r => {
            const ts = new Date(r.timestamp).toISOString();
            const prefix = this.opts.usePrefix ? `[${ts}] [${r.level.toUpperCase()}] ` : '';
            const ctx = r.context ? ` ${r.context}` : '';
            return `${prefix}${r.message}${ctx}`;
        });
        return new Blob([lines.join('\n')], { type: 'text/plain' });
    }
}
export default IndexedDBLogger;
//# sourceMappingURL=IndexedDBLogger.js.map